/* ============================================================================
   assets/ll-pokemon-hub.js
   Behaviour for sections/ll-pokemon-hub.liquid (the TCG hub).
   ============================================================================

   One asset serves every instance: each root is found by [data-lltc-root] and
   its id read from data-section-id. Per-instance data lives in the section's
   JSON config block.

   WHAT IT DOES
     Game tabs      remembered per device, opened on the game this browser has
                    been looking at, and broadcast as `ll:hub:game` so the
                    brand advert above follows along.
     Stages         phones show one stage at a time behind the Singles /
                    Sealed / Protect switch; larger screens stack them.
     Lanes          chip filters, rail arrows and edge fades.
     Sets           the set row scrolls with the same arrows and fades.
     Search         the selected game's whole catalogue via Predictive Search.
     Quick add      single-variant products straight into the basket.
     Watchlist      one list for the whole storefront (shared with the
                    pre-order hub and the header), plus a view built from the
                    saved handles so it can show cards no lane here holds.
============================================================================ */
(function () {
  /* Two instances of the section put two identical <script src> tags on the
     page, and the browser runs both. */
  if (window.__llPokemonHubJS) return;
  window.__llPokemonHubJS = true;

  var inited = {};

  var BELL = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2a6 6 0 0 0-6 6v3.6l-1.7 3.1A1 1 0 0 0 5.2 16h13.6a1 1 0 0 0 .9-1.3L18 11.6V8a6 6 0 0 0-6-6Zm0 20a3 3 0 0 0 2.8-2H9.2A3 3 0 0 0 12 22Z"/></svg>';
  var BAG = '<svg class="lltc__ico-line" viewBox="0 0 24 24" aria-hidden="true"><path d="M5.5 8.5h13l-1.1 11a1.6 1.6 0 0 1-1.6 1.5H8.2a1.6 1.6 0 0 1-1.6-1.5l-1.1-11Z"/><path d="M9 10.5V7a3 3 0 0 1 6 0v3.5"/></svg>';

  function initInstance(ROOT_ID) {
    function boot(root) {
      if (!root || root.dataset.lltcReady === '1') return;
      root.dataset.lltcReady = '1';

      var cfg = {};
      try { cfg = JSON.parse(root.querySelector('[data-lltc-config]').textContent); } catch (e) { cfg = {}; }

      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var live = root.querySelector('[data-live]');
      var observers = [];
      var railUpdaters = [];
      var cleanups = [];

      var K_AFF = 'll_tcg_affinity';
      var K_LAST = 'll_tcg_last';
      var K_RV = 'll_recently_viewed';
      /* One watchlist across the storefront. */
      var K_WATCH = 'll_watchlist';

      function say(m) { if (live) live.textContent = m; }
      function readJSON(store, key, fallback) {
        try { var v = store.getItem(key); return v ? JSON.parse(v) : fallback; } catch (e) { return fallback; }
      }
      function writeJSON(store, key, val) {
        try { store.setItem(key, JSON.stringify(val)); } catch (e) {}
      }
      function rootUrl() {
        return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      }

      /* ---------------- toast ---------------- */
      var toast = root.querySelector('[data-toast]');
      var toastText = root.querySelector('[data-toast-text]');
      var toastLink = root.querySelector('[data-toast-link]');
      var toastTimer = null;
      function showToast(msg, href, linkText) {
        if (!toast) return;
        toastText.textContent = msg;
        if (toastLink) {
          /* An empty linkText means "no link": removing the last watched item
             should not send someone to an empty watchlist. */
          if (linkText === '') {
            toastLink.hidden = true;
          } else {
            toastLink.hidden = false;
            toastLink.href = href || cfg.cartUrl || '/cart';
            toastLink.textContent = linkText || 'View basket';
          }
        }
        toast.classList.add('is-on');
        clearTimeout(toastTimer);
        toastTimer = setTimeout(function () { toast.classList.remove('is-on'); }, 4000);
        say(msg);
      }

      var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-tab]'));
      var panels = Array.prototype.slice.call(root.querySelectorAll('[data-panel]'));
      var currentTab = 0;

      function refreshLanes() { railUpdaters.forEach(function (f) { f(); }); }

      /* =====================================================================
         WATCHLIST - device-local, shared with the pre-order hub and header
      ===================================================================== */
      var watchChip = root.querySelector('[data-watch-chip]');
      var watchCount = root.querySelector('[data-watch-count]');
      var laneFilters = [];

      function readWatch() {
        var list = readJSON(localStorage, K_WATCH, null);
        if (Object.prototype.toString.call(list) === '[object Array]') return list;
        /* One-time migration from the pre-order hub's original key. */
        var old = readJSON(localStorage, 'll_preorder_watch', []);
        if (Object.prototype.toString.call(old) === '[object Array]' && old.length) {
          writeWatch(old);
          return old;
        }
        return [];
      }
      function writeWatch(list) {
        var clean = (list || []).slice(0, 100);
        writeJSON(localStorage, K_WATCH, clean);
        writeJSON(localStorage, 'll_preorder_watch', clean);
        var cid = root.getAttribute('data-customer-id');
        if (cid) writeJSON(localStorage, 'll_watchlist_c_' + cid, clean);
        try {
          document.dispatchEvent(new CustomEvent('ll:watchlist:change', { detail: { list: clean } }));
        } catch (e) {}
        return clean;
      }
      function unwatchHandle(handle) {
        var list = readWatch().filter(function (h) { return h !== handle; });
        writeWatch(list);
        try {
          var snaps = readJSON(localStorage, 'll_watchlist_snap', {});
          if (snaps && typeof snaps === 'object') {
            delete snaps[handle];
            writeJSON(localStorage, 'll_watchlist_snap', snaps);
          }
        } catch (e) {}
        if (window.LLWatchlistLive && typeof window.LLWatchlistLive.stop === 'function') {
          window.LLWatchlistLive.stop(handle);
        }
        return list;
      }

      function paintWatch() {
        var list = readWatch();
        root.querySelectorAll('[data-card]').forEach(function (card) {
          var bell = card.querySelector('[data-watch]');
          if (!bell) return;
          var on = list.indexOf(card.getAttribute('data-handle')) > -1;
          bell.setAttribute('aria-pressed', on ? 'true' : 'false');
          /* The control states what it will do next. */
          var next = bell.getAttribute(on ? 'data-watch-remove' : 'data-watch-add');
          if (next) {
            bell.setAttribute('aria-label', next);
            bell.setAttribute('title', next);
          }
        });
        if (watchChip) {
          watchChip.hidden = list.length === 0;
          watchChip.setAttribute('data-has', list.length ? '1' : '0');
          if (watchCount) watchCount.textContent = list.length;
          if (!list.length) watchChip.setAttribute('aria-pressed', 'false');
        }
      }

      root.addEventListener('click', function (e) {
        var bell = e.target.closest('[data-watch]');
        if (!bell || !root.contains(bell)) return;
        e.preventDefault();
        e.stopPropagation();
        var card = bell.closest('[data-card]');
        var handle = card.getAttribute('data-handle');
        var list = readWatch();
        if (list.indexOf(handle) > -1) {
          unwatchHandle(handle); paintWatch();
          if (watchViewOpen()) renderWatchView();
          var left = readWatch().length;
          showToast(
            cfg.watchRemoved || 'Removed from your watchlist',
            cfg.watchUrl || '/pages/watchlist',
            left ? (cfg.watchLinkLabel || 'View watchlist') : ''
          );
        } else {
          list.unshift(handle);
          writeWatch(list); paintWatch();
          bumpAffinity(gameOfCard(card), 2);
          if (window.LLWatchlistLive && typeof window.LLWatchlistLive.refresh === 'function') {
            try { window.LLWatchlistLive.refresh(); } catch (e2) {}
          }
          showToast(
            cfg.watchAdded || 'Saved to your watchlist',
            cfg.watchUrl || '/pages/watchlist',
            cfg.watchLinkLabel || 'View watchlist'
          );
        }
      });

      function refilterAll() { laneFilters.forEach(function (f) { f(); }); }

      /* =====================================================================
         WATCHLIST VIEW - built from the saved handles, so a watched card shows
         even when no lane on this page contains it.
      ===================================================================== */
      var wv = root.querySelector('[data-watch-view]');
      var wvGrid = root.querySelector('[data-watch-grid]');
      var wvEmpty = root.querySelector('[data-wv-empty]');
      var wvCount = root.querySelector('[data-wv-count]');
      var wvClose = root.querySelector('[data-wv-close]');

      function money(cents) {
        var v = (Number(cents) || 0) / 100;
        try {
          return new Intl.NumberFormat(document.documentElement.lang || 'en-GB',
            { style: 'currency', currency: cfg.currency || 'GBP' }).format(v);
        } catch (e) { return v.toFixed(2); }
      }

      /* Cached briefly: reopening the view should not re-hit the network. */
      function fetchProduct(handle) {
        var key = 'll_wv_' + handle;
        var hit = readJSON(sessionStorage, key, null);
        if (hit && hit.t && (Date.now() - hit.t) < 600000) return Promise.resolve(hit.p);
        return fetch(rootUrl() + 'products/' + handle + '.js')
          .then(function (r) { if (!r.ok) throw new Error('gone'); return r.json(); })
          .then(function (p) { writeJSON(sessionStorage, key, { t: Date.now(), p: p }); return p; });
      }

      /* The same reading the cards get from Liquid: "Card : SET - 123/456"
         becomes the card name with number and set beside it; "Game : Set -
         Product" drops the game; "Brand : Product - Variant" keeps the brand
         as context. Used for search results and the watchlist view. */
      var GAME_RE = /pok[eé]mon|lorcana|disney|one piece|magic|mtg|digimon/i;
      function parseTitle(title) {
        var t = String(title || '').replace(/：/g, ':').trim();
        var m = t.match(/^(.+?)\s:\s(.+?)\s-\s([A-Za-z]{0,6}\d[\w\/-]{0,12})$/);
        if (m) {
          var set = m[2].replace(/\s*\([^)]*\)\s*/g, ' ').trim();
          return { name: m[1].trim(), meta: m[3] + (set ? ' · ' + set : '') };
        }
        var i = t.indexOf(':');
        if (i > 0) {
          var pre = t.slice(0, i).trim();
          var rest = t.slice(i + 1).trim().replace(/ - \(/g, ' (');
          if (GAME_RE.test(pre)) {
            var d = rest.indexOf(' - ');
            if (d > 0) return { name: rest.slice(d + 3).replace(/ - /g, ' · '), meta: rest.slice(0, d) };
            return { name: rest, meta: '' };
          }
          return { name: rest.replace(/ - /g, ' · '), meta: pre };
        }
        return { name: t.replace(/ - /g, ' · '), meta: '' };
      }

      function sized(src, w) {
        if (!src) return '';
        if (src.indexOf('//') === 0) src = 'https:' + src;
        return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
      }

      function wvCard(p) {
        var el = document.createElement('article');
        el.className = 'lltc__card';
        el.setAttribute('data-card', '');
        el.setAttribute('data-handle', p.handle);
        el.setAttribute('data-url', rootUrl() + 'products/' + p.handle);
        el.setAttribute('data-title', p.title || '');

        var variant = null;
        for (var i = 0; i < (p.variants || []).length; i++) {
          if (p.variants[i].available) { variant = p.variants[i]; break; }
        }
        if (variant) el.setAttribute('data-vid', variant.id);
        var multi = (p.variants || []).length > 1;
        el.setAttribute('data-multi', multi ? 'true' : 'false');

        var img = p.featured_image || (p.images && p.images[0]) || '';
        var url = rootUrl() + 'products/' + p.handle;

        var cta;
        if (!p.available) {
          cta = '<a class="lltc__btn lltc__btn--ghost" href="' + url + '"><span class="lltc__btn-t">' +
            (cfg.soldOutLabel || 'Sold out') + '</span></a>';
        } else if (multi) {
          cta = '<a class="lltc__btn lltc__btn--ghost" href="' + url + '"><span class="lltc__btn-t">' +
            (cfg.optionsLabel || 'Choose options') + '</span></a>';
        } else {
          cta = '<button type="button" class="lltc__btn" data-add>' + BAG +
            '<span class="lltc__btn-t">' + (cfg.addLabel || 'Add') + '</span></button>';
        }

        el.innerHTML =
          '<div class="lltc__pic">' +
            '<a class="lltc__media" href="' + url + '" tabindex="-1" aria-hidden="true">' +
              (img ? '<img class="is-main" src="' + sized(img, 360) + '" alt="" width="360" height="360" loading="lazy" decoding="async">' : '') +
            '</a>' +
            '<button type="button" class="lltc__bell" data-watch aria-pressed="true">' + BELL + '</button>' +
          '</div>' +
          '<div class="lltc__body">' +
            '<p class="lltc__kick"></p>' +
            '<h4 class="lltc__name"><a href="' + url + '"></a></h4>' +
          '</div>' +
          '<div class="lltc__foot">' +
            '<p class="lltc__price">' + money(p.price) + '</p>' + cta +
          '</div>';
        /* textContent and setAttribute, never innerHTML: a title is untrusted. */
        var pt = parseTitle(p.title);
        el.querySelector('.lltc__kick').textContent = pt.meta || p.vendor || '';
        el.querySelector('.lltc__name a').textContent = pt.name || p.title || p.handle;
        el.querySelector('.lltc__name a').setAttribute('title', p.title || '');
        var im = el.querySelector('img');
        if (im) im.alt = p.title || '';
        var bell = el.querySelector('[data-watch]');
        bell.setAttribute('data-watch-add', 'Add ' + (p.title || 'this item') + ' to your watchlist');
        bell.setAttribute('data-watch-remove', 'Remove ' + (p.title || 'this item') + ' from your watchlist');
        bell.setAttribute('aria-label', bell.getAttribute('data-watch-remove'));
        addTypeTags(el, p.handle);
        return el;
      }

      /* The rail's cards get their type tags (IR, Promo, Booster box, JP) in
         Liquid, from snippets/ll-type-tags. A saved item is drawn here from
         /products/<handle>.js, which carries no metafields, so its tags are
         asked of the same snippet through templates/product.ll-tags.liquid.
         The tags sit over the photo's edge, so arriving late moves nothing. */
      function addTypeTags(el, handle) {
        if (!handle || !window.fetch) return;
        fetch(rootUrl() + 'products/' + encodeURIComponent(handle) + '?view=ll-tags', { credentials: 'same-origin' })
          .then(function (r) { return r.ok ? r.text() : ''; })
          .then(function (t) {
            var html = '';
            try { html = JSON.parse(t); } catch (e) { html = ''; }
            if (typeof html !== 'string' || !html) return;
            var pic = el.querySelector('.lltc__pic');
            if (!pic || pic.querySelector('.lltc__flags')) return;
            var flags = document.createElement('span');
            flags.className = 'lltc__flags';
            flags.innerHTML = html;
            pic.insertBefore(flags, pic.querySelector('.lltc__bell'));
          })
          .catch(function () {});
      }

      function renderWatchView() {
        if (!wv || !wvGrid) return;
        var list = readWatch();
        if (wvCount) wvCount.textContent = list.length;
        wvGrid.innerHTML = '';
        if (!list.length) {
          if (wvEmpty) wvEmpty.setAttribute('data-show', '1');
          return;
        }
        if (wvEmpty) wvEmpty.setAttribute('data-show', '0');
        wvGrid.innerHTML = '<p class="lltc__wv-skel">Loading your saved items…</p>';

        Promise.all(list.slice(0, 24).map(function (h) {
          return fetchProduct(h).catch(function () { return null; });
        })).then(function (items) {
          wvGrid.innerHTML = '';
          var gone = [];
          var shownAny = 0;
          items.forEach(function (p, i) {
            if (!p) { gone.push(list[i]); return; }
            wvGrid.appendChild(wvCard(p));
            shownAny++;
          });
          /* Prune anything that no longer exists so the count stays truthful. */
          if (gone.length) {
            var kept = readWatch().filter(function (h) { return gone.indexOf(h) === -1; });
            writeWatch(kept);
            paintWatch();
            if (wvCount) wvCount.textContent = kept.length;
          }
          if (!shownAny && wvEmpty) wvEmpty.setAttribute('data-show', '1');
        });
      }

      function watchViewOpen() { return !!(wv && !wv.hidden); }
      function openWatchView() {
        if (!wv) return;
        wv.hidden = false;
        panels.forEach(function (p) { p.hidden = true; });
        if (watchChip) watchChip.setAttribute('aria-pressed', 'true');
        renderWatchView();
        say('Showing your watchlist');
        wv.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'nearest' });
      }
      function closeWatchView() {
        if (!wv) return;
        wv.hidden = true;
        if (watchChip) watchChip.setAttribute('aria-pressed', 'false');
        if (tabs.length) activate(currentTab, false, false);
        else panels.forEach(function (p, i) { p.hidden = i !== 0; });
        say('Back to browsing');
      }

      if (watchChip) {
        watchChip.addEventListener('click', function () {
          if (watchViewOpen()) closeWatchView(); else openWatchView();
        });
      }
      if (wvClose) wvClose.addEventListener('click', closeWatchView);

      /* Keep bells and the Watching chip in step when anything else on the
         page (header, pre-order hub, another tab) changes the list. */
      function syncWatchFromStore() {
        paintWatch();
        if (watchViewOpen()) {
          if (!readWatch().length) closeWatchView();
          else renderWatchView();
        }
        refilterAll();
      }
      function onWatchStorage(ev) {
        if (!ev || !ev.key) return;
        if (ev.key === K_WATCH || ev.key === 'll_preorder_watch' || (ev.key.indexOf && ev.key.indexOf('ll_watchlist_c_') === 0)) {
          syncWatchFromStore();
        }
      }
      document.addEventListener('ll:watchlist:change', syncWatchFromStore);
      window.addEventListener('storage', onWatchStorage);

      /* =====================================================================
         AFFINITY - which game has this browser been looking at? Device-local.
           explicit pick > quick-adds > card clicks > recently viewed > landing URL
      ===================================================================== */
      function termsOf(tab) {
        return (tab.getAttribute('data-terms') || '')
          .split(',').map(function (t) { return t.trim(); }).filter(Boolean);
      }
      function classify(text) {
        if (!text) return null;
        var t = String(text).toLowerCase();
        for (var i = 0; i < tabs.length; i++) {
          var terms = termsOf(tabs[i]);
          for (var j = 0; j < terms.length; j++) {
            if (terms[j] && t.indexOf(terms[j]) > -1) return tabs[i].getAttribute('data-game');
          }
        }
        return null;
      }
      function bumpAffinity(key, n) {
        if (!key) return;
        var aff = readJSON(localStorage, K_AFF, {}) || {};
        aff[key] = Math.min((aff[key] || 0) + n, 40);
        writeJSON(localStorage, K_AFF, aff);
      }
      function pickGame() {
        var scores = {};
        function add(key, n) { if (key) scores[key] = (scores[key] || 0) + n; }

        var last = readJSON(localStorage, K_LAST, null);
        var days = cfg.rememberDays || 30;
        if (last && last.g && last.ts && (Date.now() - last.ts) < days * 86400000) add(last.g, 60);

        var aff = readJSON(localStorage, K_AFF, {}) || {};
        Object.keys(aff).forEach(function (k) { add(k, Math.min(aff[k], 20) * 2); });

        var rv = readJSON(localStorage, K_RV, []);
        if (Object.prototype.toString.call(rv) === '[object Array]') {
          rv.slice(0, 12).forEach(function (h, i) {
            if (typeof h !== 'string') return;
            var w = 12 - i;
            add(classify(h.replace(/-/g, ' ')), w * 0.5);
            var cached = readJSON(sessionStorage, 'll_rvp_' + h, null);
            if (cached) {
              var d = cached.p || cached.product || cached.data || cached;
              var tags = d && d.tags;
              var txt = [d && d.title, d && d.vendor, d && d.type,
                (Object.prototype.toString.call(tags) === '[object Array]' ? tags.join(' ') : tags)].join(' ');
              add(classify(txt), w);
            }
          });
        }
        try {
          add(classify(decodeURIComponent(window.location.pathname).replace(/[-/_]/g, ' ')), 8);
        } catch (e) {}

        var bestKey = null, bestScore = 0;
        Object.keys(scores).forEach(function (k) {
          if (scores[k] > bestScore) { bestScore = scores[k]; bestKey = k; }
        });
        if (!bestKey) return { index: 0, key: null, score: 0 };
        for (var i = 0; i < tabs.length; i++) {
          if (tabs[i].getAttribute('data-game') === bestKey) return { index: i, key: bestKey, score: bestScore };
        }
        return { index: 0, key: null, score: 0 };
      }

      /* =====================================================================
         TABS
      ===================================================================== */
      function activate(index, focusTab, remember) {
        currentTab = index;
        if (wv && !wv.hidden) {
          wv.hidden = true;
          if (watchChip) watchChip.setAttribute('aria-pressed', 'false');
        }
        tabs.forEach(function (t, i) {
          var on = i === index;
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.setAttribute('tabindex', on ? '0' : '-1');
          if (on && focusTab) t.focus();
          if (!on) return;
          root.style.setProperty('--g', t.getAttribute('data-acc'));
          root.style.setProperty('--g-ink', t.getAttribute('data-acc-ink'));
          root.style.setProperty('--on-g', t.getAttribute('data-on-acc'));
          /* Bring the tab into view inside its own strip only. scrollIntoView
             would scroll the window too, and this runs on load. */
          for (var strip = t.parentNode; strip && strip !== root; strip = strip.parentNode) {
            if (strip.scrollWidth > strip.clientWidth + 1) {
              var sr = strip.getBoundingClientRect(), tr = t.getBoundingClientRect();
              var dx = tr.left < sr.left ? tr.left - sr.left - 12 : tr.right > sr.right ? tr.right - sr.right + 12 : 0;
              if (dx) strip.scrollBy({ left: dx, behavior: reduce ? 'auto' : 'smooth' });
              break;
            }
          }
          if (remember) {
            writeJSON(localStorage, K_LAST, { g: t.getAttribute('data-game'), ts: Date.now() });
            bumpAffinity(t.getAttribute('data-game'), 1);
          }
        });
        panels.forEach(function (p, i) { p.hidden = i !== index; });
        refreshLanes();
        var label = tabs[index] ? tabs[index].getAttribute('data-label') : '';
        if (label) say(label + ' shown');
        updateFindScope();
        if (findInput && findInput.value) {
          clearTimeout(findTimer);
          findTimer = setTimeout(function () { renderFind(findInput.value); }, 40);
        }

        /* Tell the rest of the page which game is showing, last, so listeners
           see the finished state. The brand advert above follows this. */
        var t = tabs[index];
        if (t) {
          try {
            document.dispatchEvent(new CustomEvent('ll:hub:game', {
              detail: {
                game: t.getAttribute('data-game'),
                label: t.getAttribute('data-label'),
                acc: t.getAttribute('data-acc'),
                accInk: t.getAttribute('data-acc-ink'),
                onAcc: t.getAttribute('data-on-acc'),
                index: index
              }
            }));
          } catch (e) {}
        }
      }

      /* The "opened on X" notice explains an automatic pick; once the shopper
         picks a game themselves it has nothing left to explain. */
      function dropNotice() {
        var n = root.querySelector('[data-auto]');
        if (n) n.setAttribute('data-on', '0');
      }
      tabs.forEach(function (t, i) {
        t.addEventListener('click', function () { activate(i, false, true); dropNotice(); });
        t.addEventListener('keydown', function (e) {
          var k = e.key, n = null;
          if (k === 'ArrowRight') n = (i + 1) % tabs.length;
          else if (k === 'ArrowLeft') n = (i - 1 + tabs.length) % tabs.length;
          else if (k === 'Home') n = 0;
          else if (k === 'End') n = tabs.length - 1;
          if (n !== null) { e.preventDefault(); activate(n, true, true); dropNotice(); }
        });
      });

      /* =====================================================================
         RAILS - shared by lanes and the set row: arrows, edge fades
      ===================================================================== */
      function wireRail(rail, prev, next, nav, step) {
        function update() {
          if (!rail.clientWidth) return;      /* hidden panels measure zero */
          var max = rail.scrollWidth - rail.clientWidth;
          if (max <= 4) {
            rail.setAttribute('data-edge', 'none');
            if (nav) nav.hidden = true;
            return;
          }
          if (nav) nav.hidden = false;
          var x = rail.scrollLeft;
          var atStart = x <= 2, atEnd = x >= max - 2;
          rail.setAttribute('data-edge', atStart ? 'right' : (atEnd ? 'left' : 'both'));
          if (prev) prev.disabled = atStart;
          if (next) next.disabled = atEnd;
        }
        rail.addEventListener('scroll', update, { passive: true });
        [[prev, -1], [next, 1]].forEach(function (pair) {
          if (!pair[0]) return;
          pair[0].addEventListener('click', function () {
            rail.scrollBy({
              left: pair[1] * Math.max(200, Math.round(rail.clientWidth * step)),
              behavior: reduce ? 'auto' : 'smooth'
            });
          });
        });
        if ('ResizeObserver' in window) {
          var ro = new ResizeObserver(update);
          ro.observe(rail);
          observers.push(ro);
        }
        railUpdaters.push(update);
        update();
        return update;
      }

      /* ---- per lane: chip filters + rail ---- */
      root.querySelectorAll('[data-lane]').forEach(function (lane) {
        var track = lane.querySelector('[data-track]');
        if (!track) return;
        var rail = lane.querySelector('[data-rail]');
        var empty = lane.querySelector('[data-empty]');
        var chips = Array.prototype.slice.call(lane.querySelectorAll('[data-chip]'));
        var cards = Array.prototype.slice.call(lane.querySelectorAll('[data-card]'));
        var active = [];
        var updateRail = function () {};

        function tokensOf(card) {
          var t = card.__llTok;
          if (!t) {
            t = (card.getAttribute('data-tokens') || '').split(/\s+/).filter(Boolean);
            card.__llTok = t;
          }
          return t;
        }
        function apply() {
          var visible = 0;
          cards.forEach(function (card) {
            var show = !active.length;
            if (!show) {
              var t = tokensOf(card);
              for (var i = 0; i < active.length; i++) {
                if (t.indexOf(active[i]) > -1) { show = true; break; }
              }
            }
            card.hidden = !show;
            if (show) visible++;
          });
          if (empty) empty.setAttribute('data-show', visible === 0 ? '1' : '0');
          updateRail();
        }
        /* A chip with nothing behind it is removed rather than greyed out, and
           a row with nothing left to choose between goes altogether. */
        function prune() {
          var liveChips = 0;
          chips.forEach(function (chip) {
            var token = chip.getAttribute('data-token');
            if (token === 'all') return;
            var n = cards.filter(function (c) { return tokensOf(c).indexOf(token) > -1; }).length;
            chip.hidden = n === 0;
            chip.disabled = n === 0;
            if (n > 0) liveChips++;
          });
          var group = lane.querySelector('.lltc__chips');
          if (group) group.hidden = liveChips === 0;
        }
        function paintChips() {
          chips.forEach(function (chip) {
            var token = chip.getAttribute('data-token');
            var on = token === 'all' ? active.length === 0 : active.indexOf(token) > -1;
            chip.setAttribute('aria-pressed', on ? 'true' : 'false');
          });
        }
        chips.forEach(function (chip) {
          chip.addEventListener('click', function () {
            if (chip.disabled) return;
            var token = chip.getAttribute('data-token');
            active = (token === 'all' || (active.length === 1 && active[0] === token)) ? [] : [token];
            paintChips();
            apply();
            if (rail) rail.scrollTo({ left: 0, behavior: reduce ? 'auto' : 'smooth' });
            var label = chip.textContent.replace(/\s+/g, ' ').trim();
            say(active.length ? label + ' only' : 'Showing everything');
          });
        });

        if (rail) {
          updateRail = wireRail(rail, lane.querySelector('[data-prev]'), lane.querySelector('[data-next]'), lane.querySelector('[data-nav]'), 0.85);
        }
        prune();
        paintChips();
        apply();
        laneFilters.push(apply);
      });

      /* ---- the set row ---- */
      root.querySelectorAll('[data-erawrap]').forEach(function (wrap) {
        var rail = wrap.querySelector('[data-erarail]');
        if (!rail) return;
        wireRail(rail, wrap.querySelector('[data-eraprev]'), wrap.querySelector('[data-eranext]'), wrap.querySelector('[data-eranav]'), 0.8);
      });

      /* =====================================================================
         STAGES - phones show one at a time behind the Singles / Sealed /
         Protect switch. Larger screens stack them and hide the switch.
         Progressive: without this script every lane stays stacked.
      ===================================================================== */
      var phoneMQ = window.matchMedia ? window.matchMedia('(max-width: 767px)') : null;
      var stageGroups = [];
      panels.forEach(function (panel) {
        var bar = panel.querySelector('[data-steps]');
        var lanesEl = panel.querySelector('[data-lltc-lanes]');
        if (!bar || !lanesEl) return;
        var steps = Array.prototype.slice.call(bar.querySelectorAll('[data-step]'));
        var lanes = steps.map(function (s) {
          var id = s.getAttribute('data-target');
          /* Looked up by attribute: section ids can hold selector syntax. */
          return id ? panel.querySelector('[id="' + id + '"]') : null;
        });
        if (steps.length < 2) return;

        var g = { lanesEl: lanesEl, steps: steps, lanes: lanes, cur: 0 };
        g.show = function (i, focus) {
          if (!lanes[i]) return;
          g.cur = i;
          lanes.forEach(function (l, j) { if (l) l.classList.toggle('is-on', i === j); });
          steps.forEach(function (s, j) {
            s.setAttribute('aria-selected', i === j ? 'true' : 'false');
            s.setAttribute('tabindex', i === j ? '0' : '-1');
            if (i === j && focus) s.focus();
          });
          refreshLanes();
        };
        g.apply = function () {
          if (phoneMQ && phoneMQ.matches) lanesEl.setAttribute('data-one', '1');
          else lanesEl.removeAttribute('data-one');
          g.show(g.cur);
        };
        steps.forEach(function (s, i) {
          s.addEventListener('click', function () {
            g.show(i);
            say(s.textContent.replace(/\s+/g, ' ').trim() + ' shown');
          });
          s.addEventListener('keydown', function (e) {
            var n = null;
            if (e.key === 'ArrowRight') n = (i + 1) % steps.length;
            else if (e.key === 'ArrowLeft') n = (i - 1 + steps.length) % steps.length;
            if (n !== null) { e.preventDefault(); g.show(n, true); }
          });
        });
        lanes.forEach(function (l, j) { if (l) { l.__llStage = g; l.__llStageIndex = j; } });
        g.apply();
        stageGroups.push(g);
      });
      function applyStages() { stageGroups.forEach(function (g) { g.apply(); }); }
      if (phoneMQ) {
        if (phoneMQ.addEventListener) phoneMQ.addEventListener('change', applyStages);
        else if (phoneMQ.addListener) phoneMQ.addListener(applyStages);
        cleanups.push(function () {
          if (phoneMQ.removeEventListener) phoneMQ.removeEventListener('change', applyStages);
        });
      }

      /* =====================================================================
         INTERACTIONS THAT TEACH THE HUB, and quick add
      ===================================================================== */
      function gameOfCard(card) {
        var panel = card.closest('[data-panel]');
        return panel ? panel.getAttribute('data-game') : null;
      }

      root.addEventListener('click', function (e) {
        var card = e.target.closest('[data-card]');
        if (card && root.contains(card) && !e.target.closest('[data-add]') && !e.target.closest('[data-watch]')) {
          bumpAffinity(gameOfCard(card), 1);
        }
      });

      root.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-add]');
        if (!btn || !root.contains(btn)) return;
        e.preventDefault();
        var card = btn.closest('[data-card]');
        var id = card && card.getAttribute('data-vid');
        if (!id) { if (card) window.location.href = card.getAttribute('data-url'); return; }
        if (btn.classList.contains('is-busy')) return;

        btn.classList.add('is-busy');
        fetch(rootUrl() + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
        })
          .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, json: j }; }); })
          .then(function (res) {
            if (!res.ok) throw new Error(res.json.description || res.json.message || 'Could not add');
            bumpAffinity(gameOfCard(card), 4);
            btn.classList.add('is-done');
            setTimeout(function () { btn.classList.remove('is-done'); }, 1600);
            showToast(cfg.added || 'Added to your basket', cfg.cartUrl, 'View basket');
            document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
            document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
          })
          .catch(function (err) { showToast(err.message || 'Sorry, that could not be added', card.getAttribute('data-url'), 'View item'); })
          .then(function () { btn.classList.remove('is-busy'); });
      });

      /* =====================================================================
         HUB SEARCH - the selected game's full catalogue via Predictive Search
      ===================================================================== */
      var findWrap = root.querySelector('[data-find]');
      var findInput = root.querySelector('[data-find-input]');
      var findDrop = root.querySelector('[data-find-drop]');
      var findList = root.querySelector('[data-find-list]');
      var findHint = root.querySelector('[data-find-hint]');
      var findEmpty = root.querySelector('[data-find-empty]');
      var findClear = root.querySelector('[data-find-clear]');
      var findScope = root.querySelector('[data-find-scope]');
      var findMore = root.querySelector('[data-find-more]');
      var findTimer = null;
      var findActive = -1;
      var findHits = [];
      var findPulseTimer = null;
      var findReq = 0;

      function esc(s) { return String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;'); }
      function normFind(s) {
        return String(s || '')
          .toLowerCase()
          .replace(/[éèêë]/g, 'e').replace(/[áàâä]/g, 'a').replace(/[íìîï]/g, 'i')
          .replace(/[óòôö]/g, 'o').replace(/[úùûü]/g, 'u').replace(/ñ/g, 'n')
          .replace(/[^a-z0-9\s\/\-:]/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
      }
      function splitAttr(el, name) {
        return (el && el.getAttribute(name) || '')
          .split(',').map(function (t) { return normFind(t); }).filter(Boolean);
      }
      function activeFindTab() { return tabs[currentTab] || tabs[0] || null; }
      function updateFindScope() {
        var tab = activeFindTab();
        if (!tab) return;
        var label = tab.getAttribute('data-label') || 'this game';
        if (findInput) {
          findInput.placeholder = String(cfg.searchPlaceholderGame || 'Search all %game%').replace('%game%', label);
          findInput.setAttribute('aria-label', 'Search all ' + label);
        }
        if (findScope) findScope.textContent = String(cfg.searchScope || 'Searching all %game%').replace('%game%', label);
        if (findMore) {
          var curl = tab.getAttribute('data-collection-url') || '';
          findMore.textContent = (cfg.searchMore || 'Browse full collection') + (label ? ': ' + label : '');
          if (curl) { findMore.href = curl; findMore.hidden = false; } else { findMore.hidden = true; }
        }
      }
      function productBelongsToGame(product, tab) {
        if (!product || !tab) return false;
        var terms = splitAttr(tab, 'data-terms');
        var tags = splitAttr(tab, 'data-tags');
        var types = splitAttr(tab, 'data-types');
        var vendors = splitAttr(tab, 'data-vendors');
        var collections = splitAttr(tab, 'data-collections');
        var type = normFind(product.type || product.product_type || '');
        var vendor = normFind(product.vendor || '');
        var title = normFind(product.title || '');
        var url = normFind(product.url || '');
        var tagList = [];
        if (Object.prototype.toString.call(product.tags) === '[object Array]') tagList = product.tags.map(normFind);
        else if (product.tags) tagList = String(product.tags).split(',').map(normFind);
        var i;
        for (i = 0; i < types.length; i++) {
          if (types[i] && type && (type === types[i] || type.indexOf(types[i]) > -1)) return true;
        }
        for (i = 0; i < tags.length; i++) {
          if (tags[i] && tagList.indexOf(tags[i]) > -1) return true;
        }
        for (i = 0; i < vendors.length; i++) {
          if (vendors[i] && vendor.indexOf(vendors[i]) > -1) return true;
        }
        for (i = 0; i < collections.length; i++) {
          if (collections[i] && url.indexOf('/collections/' + collections[i]) > -1) return true;
        }
        for (i = 0; i < terms.length; i++) {
          if (!terms[i] || terms[i].length < 3) continue;
          if (title.indexOf(terms[i]) > -1 || url.indexOf(terms[i].replace(/\s+/g, '-')) > -1) return true;
          if (type.indexOf(terms[i]) > -1 || vendor.indexOf(terms[i]) > -1) return true;
          if (tagList.some(function (t) { return t.indexOf(terms[i]) > -1; })) return true;
        }
        return false;
      }
      function scoreProduct(product, q) {
        var title = normFind(product.title || '');
        var score;
        if (title === q) score = 200;
        else if (title.indexOf(q) === 0) score = 160;
        else if ((' ' + title + ' ').indexOf(' ' + q + ' ') > -1) score = 130;
        else if (title.indexOf(q) > -1) score = 100;
        else score = 50;
        if (product.available === false) score -= 8;
        return score;
      }
      function hubCardForHandle(handle, url) {
        var cards = root.querySelectorAll('[data-lane] [data-card]');
        for (var i = 0; i < cards.length; i++) {
          if (handle && cards[i].getAttribute('data-handle') === handle) return cards[i];
          if (url && cards[i].getAttribute('data-url') === url) return cards[i];
        }
        return null;
      }
      function openFindDrop(open) {
        if (!findDrop || !findInput) return;
        findDrop.hidden = !open;
        findDrop.setAttribute('data-open', open ? '1' : '0');
        findInput.setAttribute('aria-expanded', open ? 'true' : 'false');
      }
      function paintFindActive() {
        if (!findList) return;
        Array.prototype.forEach.call(findList.querySelectorAll('[data-find-item]'), function (btn, i) {
          btn.setAttribute('aria-selected', i === findActive ? 'true' : 'false');
          if (i === findActive) btn.scrollIntoView({ block: 'nearest' });
        });
      }
      function paintFindHits(gameLabel) {
        if (!findList) return;
        if (findHint) findHint.hidden = true;
        if (findScope) findScope.hidden = false;
        if (!findHits.length) {
          findList.hidden = true;
          findList.innerHTML = '';
          if (findEmpty) findEmpty.hidden = false;
          openFindDrop(true);
          say('No matches in ' + (gameLabel || 'this game'));
          return;
        }
        if (findEmpty) findEmpty.hidden = true;
        findList.hidden = false;
        findList.innerHTML = findHits.map(function (item, i) {
          var pt = parseTitle(item.title);
          var meta = [pt.meta || item.type, item.price].filter(Boolean).join(' · ');
          var thumb = item.img
            ? '<img class="lltc__find-thumb" src="' + esc(item.img) + '" alt="" width="44" height="44" loading="lazy">'
            : '<span class="lltc__find-thumb lltc__find-thumb--empty" aria-hidden="true"></span>';
          return '<li role="presentation">' +
            '<button type="button" class="lltc__find-item" role="option" data-find-item="' + i + '" aria-selected="false">' +
              thumb +
              '<span class="lltc__find-text"><span class="lltc__find-name" title="' + esc(item.title) + '">' + esc(pt.name || item.title) + '</span>' +
              (meta ? '<span class="lltc__find-meta">' + esc(meta) + '</span>' : '') +
              '</span>' +
              (item.soldOut ? '<span class="lltc__find-flag">Sold out</span>' : '') +
            '</button></li>';
        }).join('');
        openFindDrop(true);
        say(findHits.length + ' match' + (findHits.length === 1 ? '' : 'es') + ' in ' + (gameLabel || 'this game'));
      }
      function fetchPredictive(query, tab) {
        var types = splitAttr(tab, 'data-types');
        var tags = splitAttr(tab, 'data-tags');
        var label = tab.getAttribute('data-label') || '';
        function fieldOp(key, val) {
          if (!val) return '';
          return /\s/.test(val) ? (' ' + key + ':"' + val + '"') : (' ' + key + ':' + val);
        }
        var scoped = query;
        if (types[0]) scoped = query + fieldOp('product_type', types[0]);
        else if (tags[0]) scoped = query + fieldOp('tag', tags[0]);
        else if (label) scoped = query + ' ' + label;

        function req(q) {
          var url = rootUrl() + 'search/suggest.json?q=' + encodeURIComponent(q) +
            '&resources[type]=product' +
            '&resources[limit]=20' +
            '&resources[options][unavailable_products]=last' +
            '&resources[options][fields]=title,product_type,vendor,variants.title,tag';
          return fetch(url, { headers: { 'Accept': 'application/json' } })
            .then(function (r) { return r.ok ? r.json() : { resources: { results: { products: [] } } }; })
            .catch(function () { return { resources: { results: { products: [] } } }; });
        }
        return req(scoped).then(function (data) {
          var products = (((data || {}).resources || {}).results || {}).products || [];
          var filtered = products.filter(function (p) { return productBelongsToGame(p, tab); });
          if (filtered.length) return filtered;
          /* Broaden: plain query, still filtered to this game client-side. */
          return req(query).then(function (data2) {
            var products2 = (((data2 || {}).resources || {}).results || {}).products || [];
            return products2.filter(function (p) { return productBelongsToGame(p, tab); });
          });
        });
      }
      function renderFind(q) {
        if (!findList || !findDrop) return;
        var tab = activeFindTab();
        var query = normFind(q);
        var gameLabel = tab ? (tab.getAttribute('data-label') || '') : '';
        findHits = [];
        findActive = -1;
        if (findClear) findClear.setAttribute('data-on', query ? '1' : '0');
        updateFindScope();

        if (!query) {
          findList.hidden = true;
          findList.innerHTML = '';
          if (findHint) findHint.hidden = false;
          if (findEmpty) findEmpty.hidden = true;
          if (findScope) findScope.hidden = true;
          openFindDrop(document.activeElement === findInput);
          return;
        }
        if (!tab) {
          if (findHint) findHint.hidden = true;
          if (findEmpty) findEmpty.hidden = false;
          openFindDrop(true);
          return;
        }
        if (findHint) findHint.hidden = true;
        if (findScope) findScope.hidden = false;
        if (findEmpty) findEmpty.hidden = true;
        findList.hidden = false;
        findList.innerHTML = '<li class="lltc__find-wait" role="presentation">Searching ' + esc(gameLabel) + '…</li>';
        openFindDrop(true);

        var reqId = ++findReq;
        fetchPredictive(query, tab).then(function (products) {
          if (reqId !== findReq) return;
          var ranked = (products || []).map(function (p) {
            return { product: p, score: scoreProduct(p, query) };
          }).sort(function (a, b) { return b.score - a.score; });

          var seen = {};
          findHits = [];
          ranked.forEach(function (r) {
            var p = r.product;
            var handle = p.handle || '';
            var key = handle || p.url || p.title;
            if (seen[key]) return;
            seen[key] = 1;
            var img = '';
            if (p.image) img = typeof p.image === 'string' ? p.image : (p.image + '');
            if (!img && p.featured_image) img = typeof p.featured_image === 'string' ? p.featured_image : (p.featured_image.url || '');
            var price = '';
            if (p.price != null && p.price !== '') {
              var pn = Number(p.price);
              if (!isNaN(pn)) price = String(p.price).indexOf('.') > -1 ? money(Math.round(pn * 100)) : money(pn);
            }
            findHits.push({
              title: p.title || '',
              url: p.url || (handle ? rootUrl() + 'products/' + handle : ''),
              handle: handle,
              img: img,
              type: p.type || p.product_type || '',
              price: price,
              soldOut: p.available === false
            });
          });
          findHits = findHits.slice(0, 8);
          if (findMore) {
            var base = tab.getAttribute('data-collection-url') || '';
            findMore.href = base || (rootUrl() + 'search?type=product&q=' + encodeURIComponent(q));
            findMore.hidden = false;
          }
          paintFindHits(gameLabel);
        });
      }
      function pulseCard(card) {
        root.querySelectorAll('.lltc__card.is-find-hit').forEach(function (c) { c.classList.remove('is-find-hit'); });
        if (!card) return;
        card.classList.add('is-find-hit');
        clearTimeout(findPulseTimer);
        findPulseTimer = setTimeout(function () { card.classList.remove('is-find-hit'); }, 2600);
      }
      function goToFindHit(item) {
        if (!item) return;
        if (watchViewOpen()) closeWatchView();
        var card = hubCardForHandle(item.handle, item.url);
        if (card) {
          var panel = card.closest('[data-panel]');
          var panelIndex = panels.indexOf(panel);
          if (panelIndex > -1 && panelIndex !== currentTab) activate(panelIndex, false, true);
          var lane = card.closest('[data-lane]');
          if (lane && lane.__llStage) lane.__llStage.show(lane.__llStageIndex);
          card.hidden = false;
          try { card.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', inline: 'center', block: 'center' }); } catch (e) {}
          pulseCard(card);
          openFindDrop(false);
          if (findInput) findInput.blur();
          say((item.title || 'Product') + ' found');
          return;
        }
        if (item.url) window.location.href = item.url;
      }

      if (findInput && cfg.enableSearch !== false) {
        updateFindScope();
        findInput.addEventListener('input', function () {
          clearTimeout(findTimer);
          findTimer = setTimeout(function () { renderFind(findInput.value); }, 160);
        });
        findInput.addEventListener('focus', function () {
          updateFindScope();
          renderFind(findInput.value);
        });
        findInput.addEventListener('keydown', function (e) {
          if (e.key === 'ArrowDown') {
            if (!findHits.length) return;
            e.preventDefault();
            findActive = (findActive + 1) % findHits.length;
            paintFindActive();
          } else if (e.key === 'ArrowUp') {
            if (!findHits.length) return;
            e.preventDefault();
            findActive = (findActive - 1 + findHits.length) % findHits.length;
            paintFindActive();
          } else if (e.key === 'Enter') {
            e.preventDefault();
            if (findHits.length) { goToFindHit(findHits[findActive > -1 ? findActive : 0]); return; }
            if (findInput.value.trim()) window.location.href = rootUrl() + 'search?type=product&q=' + encodeURIComponent(findInput.value.trim());
          } else if (e.key === 'Escape') {
            e.preventDefault();
            findInput.value = '';
            renderFind('');
            openFindDrop(false);
            findInput.blur();
          }
        });
        if (findList) {
          findList.addEventListener('click', function (e) {
            var btn = e.target.closest('[data-find-item]');
            if (!btn) return;
            var i = Number(btn.getAttribute('data-find-item'));
            if (!isNaN(i) && findHits[i]) goToFindHit(findHits[i]);
          });
        }
        if (findClear) {
          findClear.addEventListener('click', function (e) {
            e.preventDefault();
            findInput.value = '';
            renderFind('');
            findInput.focus();
          });
        }
        var onDocClick = function (e) {
          if (!findWrap || findWrap.contains(e.target)) return;
          openFindDrop(false);
        };
        document.addEventListener('click', onDocClick);
        cleanups.push(function () { document.removeEventListener('click', onDocClick); });
      }

      /* =====================================================================
         OPENING TAB
      ===================================================================== */
      var notice = root.querySelector('[data-auto]');
      var noticeName = root.querySelector('[data-auto-game]');
      var noticeClear = root.querySelector('[data-auto-clear]');

      if (tabs.length) {
        var chosen = { index: 0, key: null, score: 0 };
        if (cfg.autoSelect) {
          try { chosen = pickGame(); } catch (e) { chosen = { index: 0, key: null, score: 0 }; }
        }
        activate(chosen.index, false, false);
        /* Only say something when the pick moved them off the first tab. */
        if (notice && chosen.index > 0 && chosen.score > 0) {
          if (noticeName) noticeName.textContent = tabs[chosen.index].getAttribute('data-label');
          notice.setAttribute('data-on', '1');
        }
      } else {
        updateFindScope();
      }

      paintWatch();

      if (noticeClear) {
        noticeClear.addEventListener('click', function () {
          try { localStorage.removeItem(K_AFF); localStorage.removeItem(K_LAST); } catch (e) {}
          if (notice) notice.setAttribute('data-on', '0');
          activate(0, false, false);
          say('Reset. Showing ' + (tabs[0] ? tabs[0].getAttribute('data-label') : 'the first game'));
        });
      }

      window.addEventListener('resize', refreshLanes);

      root.lltcDestroy = function () {
        observers.forEach(function (o) { o.disconnect(); });
        cleanups.forEach(function (f) { try { f(); } catch (e) {} });
        window.removeEventListener('resize', refreshLanes);
        document.removeEventListener('ll:watchlist:change', syncWatchFromStore);
        window.removeEventListener('storage', onWatchStorage);
        clearTimeout(toastTimer);
        clearTimeout(findTimer);
        clearTimeout(findPulseTimer);
        root.dataset.lltcReady = '';
      };
    }

    function find() { return document.querySelector('[data-lltc-root][data-section-id="' + ROOT_ID + '"]'); }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () { boot(find()); });
    } else {
      boot(find());
    }
    document.addEventListener('shopify:section:load', function (e) {
      if (String(e.detail.sectionId) !== ROOT_ID) return;
      boot(find());
    });
    document.addEventListener('shopify:section:unload', function (e) {
      if (String(e.detail.sectionId) !== ROOT_ID) return;
      var el = find();
      if (el && el.lltcDestroy) el.lltcDestroy();
    });
  }

  function initOnce(id) {
    if (!id || inited[id]) return;
    inited[id] = true;
    initInstance(id);
  }
  function bootAll() {
    var roots = document.querySelectorAll('[data-lltc-root][data-section-id]');
    for (var i = 0; i < roots.length; i++) initOnce(roots[i].getAttribute('data-section-id'));
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bootAll);
  else bootAll();

  /* A section added in the theme editor arrives after first boot. */
  document.addEventListener('shopify:section:load', bootAll);
  document.addEventListener('shopify:section:unload', function (e) {
    var id = e && e.detail && String(e.detail.sectionId);
    if (id) delete inited[id];
  });
})();
